<template>
  <BaseTable
    v-if="priceCatalogs"
    :headers="priceCatalogHeaders"
    :list="priceCatalogs"
    :hide-grouping="true"
    @add-new="onAddNewCatalog"
    @deleteMultiselect="onDeleteMultiselect"
    @refresh-data="loadData"
    clickable
    @table-item-clicked="onCatalogClicked"
    table-id="routes"
    @apply-filters="onApplyFilters"
    @reset-filters="onResetFilters">
    <template #actions="{ item }: any">
      <div class="flex items-center justify-center gap-1">
        <button
          v-if="item.isFromManager"
          type="button"
          class="rounded-full p-1.5 text-red-400 hover:bg-red-50"
          @click="onDeleteRow(item as PriceCatalog)">
          <IconTrashOutline class="size-4" />
        </button>
      </div>
    </template>
    <template #filters>
      <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
        <div>
          <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
            $t('baseObject.isActive')
          }}</label>
          <BaseTriStateToggle
            :label="$t('baseObject.isActive')"
            name="isActive"
            v-model="filterIsActive" />
        </div>
      </section>
    </template>
  </BaseTable>
  <BaseModalConfirm
    v-if="modalDeleteRow"
    @cancel="modalDeleteRow = false"
    @confirm="onDeleteSingle()" />
</template>
<script setup lang="ts">
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';
import { type PriceCatalog } from '@/types/PricingTypes';
import { onMounted, ref } from 'vue';
import type { TableFilterData } from '@/types/Base';
import { filterActive, filterData, sortData } from '@/utils/table';
import { getPriceCatalogs, removePriceCatalogsByIds } from '@/services/api/prices';
import { useToastStore } from '@/stores/toast';
import { useRouter } from 'vue-router';
import { isoDateToFullDate } from '@/utils/date';
import { v4 as uuidv4 } from 'uuid';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n({ useScope: 'global' });
const toastStore = useToastStore();
const router = useRouter();
const priceCatalogHeaders = [
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'startDate', text: $t('price-catalog.startDate'), type: 'text', active: true },
  { value: 'endDate', text: $t('price-catalog.endDate'), type: 'text', active: true },
  { value: 'includeVat', text: $t('price-catalog.includeVat'), type: 'check', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];
const priceCatalogs = ref<PriceCatalog[]>([]);
const modalDeleteRow = ref(false);
const deleteRowItem = ref<PriceCatalog>();
const filterIsActive = ref<boolean | undefined>(undefined);

onMounted(async () => {
  await loadData();
});

async function loadData(data?: TableFilterData) {
  let response = await getPriceCatalogs();
  if (!response) return;

  if (data && data.query) response = filterData(response, data.query, ['name', 'code']);
  if (data && data.column !== undefined && data.order)
    response = sortData(response, data.column, data.order, priceCatalogHeaders);

  response = response.map((item) => ({
    ...item,
    startDate: isoDateToFullDate(item.startDate) || '',
    endDate: isoDateToFullDate(item.endDate) || '',
  }));

  if (data && data.isActive !== undefined) response = filterActive(response, data.isActive);
  priceCatalogs.value = response;
}

function onAddNewCatalog() {
  router.push({ name: 'price-catalog-details', params: { id: uuidv4() } });
}

function onDeleteRow(item: PriceCatalog) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removePriceCatalogsByIds([idToDelete]);
  if (!res && priceCatalogs.value)
    priceCatalogs.value = priceCatalogs.value.filter((catalog) => catalog.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

async function onDeleteMultiselect(items: PriceCatalog[]) {
  const idsToDelete = items.filter((item) => item.isFromManager === true).map((item) => item.id);
  const res = await removePriceCatalogsByIds(idsToDelete);
  if (!res && priceCatalogs.value)
    priceCatalogs.value = priceCatalogs.value.filter(
      (catalog) => !catalog.id || !idsToDelete.includes(catalog.id)
    );
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

function onCatalogClicked(item: PriceCatalog) {
  router.push({ name: 'price-catalog-details', params: { id: item.id } });
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  loadData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  loadData({});
}
</script>
